import { HTTPError } from 'ky';

import {
  CreateReferralCodeResponse,
  PointsActivitiesResponse,
  PointsLeaderboardResponse,
  PointsOpportunitiesResponse,
  PointsStatusResponse,
  ProposalResponse,
  QuoteSwapRequest,
  QuoteSwapResponse,
  ReferralCodeCheckResponse,
  ReferralInviteesResponse,
  ReferralSummaryResponse,
  SignedReferralRequest,
  UseReferralCodeResponse,
  UserPointsActivityResponse,
  UserPointsSummaryResponse,
  UserPositionsResponse,
  UserPortfolio,
  UserPendingWithdrawal,
  UserPendingWithdrawalsResponse,
  UserTransaction,
  UserTransactionsResponse,
  VaultApiMode,
  VaultPerformanceRange,
  VaultPerformanceResponse,
  VaultsResponse,
} from '@/app/shared/services/api/types';
import { kyInstance } from '@/app/shared/services/ky';

const isNotFoundError = (error: unknown): boolean =>
  error instanceof HTTPError && error.response.status === 404;

export class ReferralRequestError extends Error {
  status: number;
  reason?: string;

  constructor(status: number, message: string, reason?: string) {
    super(message);
    this.name = 'ReferralRequestError';
    this.status = status;
    this.reason = reason;
  }
}

const throwReferralRequestError = async (error: unknown): Promise<never> => {
  if (error instanceof HTTPError) {
    let message = error.message;
    let reason: string | undefined;

    try {
      const body = (await error.response.json()) as {
        message?: string | string[];
        reason?: string;
      };
      if (Array.isArray(body.message)) {
        message = body.message.join(', ');
      } else if (typeof body.message === 'string' && body.message) {
        message = body.message;
      }
      reason = body.reason;
    } catch {
      void 0;
    }

    throw new ReferralRequestError(error.response.status, message, reason);
  }

  throw error;
};

export const getUserTransactions = async (address?: string): Promise<UserTransactionsResponse | null> => {
  if (!address) return null;
  try {
    const response = await kyInstance
      .get(`api/user/${address}/transactions?limit=100`)
      .json<UserTransaction[] | UserTransactionsResponse>();

    if (Array.isArray(response)) {
      return {
        user: address,
        count: response.length,
        transactions: response,
      };
    }

    return response ?? null;
  } catch (error) {
    console.error('Error fetching user transactions:', error);
    return null;
  }
};

export const getUserPositions = async ({
  address,
  vault,
}: {
  address?: string;
  vault?: string;
}): Promise<UserPositionsResponse | null> => {
  if (!address) return null;
  try {
    const searchParams = vault ? `?vault=${vault}` : '';
    const response = await kyInstance
      .get(`api/user/${address}/positions${searchParams}`)
      .json<UserPositionsResponse>();

    return response?.positions ? response : null;
  } catch (error) {
    console.error('Error fetching user positions:', error);
    return null;
  }
};

export const getUserPortfolio = async ({
  address,
  vault,
}: {
  address?: string;
  vault?: string;
}): Promise<UserPortfolio | null> => {
  if (!address) return null;
  try {
    const searchParams = vault ? `?vault=${vault}` : '';
    const response = await kyInstance
      .get(`api/user/${address}/portfolio${searchParams}`)
      .json<UserPortfolio>();

    return response ?? null;
  } catch (error) {
    console.error('Error fetching user portfolio:', error);
    return null;
  }
};

export const getUserPendingWithdrawals = async ({
  address,
  limit = 100,
  offset = 0,
}: {
  address?: string;
  limit?: number;
  offset?: number;
}): Promise<UserPendingWithdrawalsResponse | null> => {
  if (!address) return null;
  try {
    const response = await kyInstance
      .get(`api/user/${address}/pending-withdrawals?limit=${limit}&offset=${offset}`)
      .json<UserPendingWithdrawal[] | { withdrawals: UserPendingWithdrawal[] }>();

    if (Array.isArray(response)) return response;
    return response?.withdrawals ?? null;
  } catch (error) {
    console.error('Error fetching user pending withdrawals:', error);
    return null;
  }
};

export const getLatestProposal = async (): Promise<ProposalResponse | null> => {
  try {
    const response = await kyInstance.get('api/proposal/latest').json<ProposalResponse>();
    return response ?? null;
  } catch (error) {
    if (!(error instanceof HTTPError && error.response.status === 404)) {
      console.error('Error fetching proposal:', error);
    }
    return null;
  }
};

export const getVaults = async (mode: VaultApiMode = 'current'): Promise<VaultsResponse | null> => {
  try {
    const response = await kyInstance.get(`api/vault?mode=${mode}`).json<VaultsResponse>();
    return response ?? null;
  } catch (error) {
    console.error('Error fetching vaults:', error);
    return null;
  }
};

export const getVaultPerformance = async ({
  id,
  range = 'all',
}: {
  id?: string;
  range?: VaultPerformanceRange;
}): Promise<VaultPerformanceResponse | null> => {
  if (!id) return null;
  try {
    const response = await kyInstance
      .get(`api/vault/${id}/performance?range=${range}`)
      .json<VaultPerformanceResponse>();
    return response ?? null;
  } catch (error) {
    if (isNotFoundError(error)) {
      return {
        vaultId: id,
        range,
        granularity: '',
        from: '',
        to: '',
        tranches: [],
      };
    }
    console.error('Error fetching vault performance:', error);
    return null;
  }
};

// Mezzanine Points

export const getPointsLeaderboard = async ({
  page = 1,
  limit = 100,
  address,
}: {
  page?: number;
  limit?: number;
  address?: string;
} = {}): Promise<PointsLeaderboardResponse | null> => {
  try {
    const searchParams = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (address) searchParams.set('address', address);

    const response = await kyInstance
      .get(`api/points/leaderboard?${searchParams.toString()}`)
      .json<PointsLeaderboardResponse>();

    if (response?.error) return null;
    return response ?? null;
  } catch (error) {
    console.error('Error fetching points leaderboard:', error);
    return null;
  }
};

export const getPointsActivities = async (): Promise<PointsActivitiesResponse | null> => {
  try {
    const response = await kyInstance.get('api/points/activities').json<PointsActivitiesResponse>();
    return response ?? null;
  } catch (error) {
    console.error('Error fetching points activities:', error);
    return null;
  }
};

export const getPointsOpportunities = async (): Promise<PointsOpportunitiesResponse | null> => {
  try {
    const response = await kyInstance.get('api/points/opportunities').json<PointsOpportunitiesResponse>();
    return response ?? null;
  } catch (error) {
    console.error('Error fetching points opportunities:', error);
    return null;
  }
};

export const getPointsStatus = async (): Promise<PointsStatusResponse | null> => {
  try {
    const response = await kyInstance.get('api/points/status').json<PointsStatusResponse>();
    return response ?? null;
  } catch (error) {
    console.error('Error fetching points status:', error);
    return null;
  }
};

export const getUserPointsActivity = async (address?: string): Promise<UserPointsActivityResponse | null> => {
  if (!address) return null;
  try {
    const response = await kyInstance
      .get(`api/points/your-activity/${address}?limit=100`)
      .json<UserPointsActivityResponse>();
    return response ?? null;
  } catch (error) {
    console.error('Error fetching user points activity:', error);
    return null;
  }
};

export const getUserPointsSummary = async (address?: string): Promise<UserPointsSummaryResponse | null> => {
  if (!address) return null;
  try {
    const response = await kyInstance.get(`api/points/${address}`).json<UserPointsSummaryResponse>();
    return response ?? null;
  } catch (error) {
    console.error('Error fetching user points summary:', error);
    return null;
  }
};

export const getQuoteSwap = async (params: QuoteSwapRequest): Promise<QuoteSwapResponse | null> => {
  if (!params.userAddress) return null;

  try {
    const response = await kyInstance
      .post('api/swap/quote', {
        json: params,
      })
      .json<QuoteSwapResponse>();

    return response ?? null;
  } catch (error) {
    console.error('Error Swap:', error);
    return null;
  }
};

// Referrals

export const getReferralSummary = async (address?: string): Promise<ReferralSummaryResponse | null> => {
  if (!address) return null;
  try {
    return await kyInstance.get(`api/referral/${address}`).json<ReferralSummaryResponse>();
  } catch (error) {
    console.error('Error fetching referral summary:', error);
    return null;
  }
};

export const getReferralInvitees = async ({
  address,
  page = 1,
  limit = 100,
}: {
  address?: string;
  page?: number;
  limit?: number;
}): Promise<ReferralInviteesResponse | null> => {
  if (!address) return null;
  try {
    const searchParams = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    return await kyInstance
      .get(`api/referral/${address}/invitees?${searchParams.toString()}`)
      .json<ReferralInviteesResponse>();
  } catch (error) {
    console.error('Error fetching referral invitees:', error);
    return null;
  }
};

export const getReferralCodeCheck = async (code?: string): Promise<ReferralCodeCheckResponse | null> => {
  if (!code) return null;
  try {
    return await kyInstance.get(`api/referral/codes/${encodeURIComponent(code)}`).json<ReferralCodeCheckResponse>();
  } catch (error) {
    console.error('Error checking referral code:', error);
    return null;
  }
};

export const createReferralCode = async (body: SignedReferralRequest): Promise<CreateReferralCodeResponse> => {
  try {
    return await kyInstance
      .post('api/referral/codes', { json: body })
      .json<CreateReferralCodeResponse>();
  } catch (error) {
    return throwReferralRequestError(error);
  }
};

export const bindReferralCode = async (body: SignedReferralRequest): Promise<UseReferralCodeResponse> => {
  try {
    return await kyInstance.post('api/referral/use', { json: body }).json<UseReferralCodeResponse>();
  } catch (error) {
    return throwReferralRequestError(error);
  }
};
